import { useMediaQuery } from "hooks/use-media-query/use-media-query";
import * as Sentry from "@sentry/react";
import Link from "next/link";
import WhatAppIcon from "public/assets/whatsapp.png";
import { useRouter } from "next/router";

import React, { useEffect, useState } from "react";
import { eventNames } from "services/mixpanel/events";
import trackEvent from "services/mixpanel/mixpanel";
import { getUserProfiles } from "services/api/users/user-services";
import { useAuth } from "stores/auth/auth-context";
import { useCookies } from "react-cookie";
import {
  getPageNameFromPath,
  isKelasLivePage,
  isMarketingPage
} from "utils/utils";
import { constants } from "../../constants/constants";

import { IPrimaryAuthData } from "../../services/api/auth/authService.types";

import { Close } from "components/icons/close";
import { Hamburger } from "components/icons/hamburger";
import { Logo } from "../logo/logo";
import {
  DownloadButton,
  DownloadButton2,
  HamburgerIcon,
  HeaderCTA,
  HeaderContainer,
  HeaderMenuCTA,
  Menu,
  MenuContainer,
  MenuItem,
  MenuLink,
  MobileMenuContainer,
  StyledHeader,
  MobileUserProfileContainer,
  LeadGenerationButton,
  Spacer
} from "./header.styles";
import { IHeaderProps } from "./header.types";
import { Badge } from "design-system/components/badge/badge-new-feature/badge-new-feature";
import { mergeArraysByKey } from "utils/utils";
import { MobileUserProfile, UserProfile } from "./user-profile/user-profile";
import ContactMe from "components/contact-me/contact-me"; // Import ContactMe modal component
import { useTanyaContext } from "stores/tanya/tanya-context"; // Ensure this is imported
import { getLeadSourceFromPath } from "utils/utils";
import Image from "next/image";
import { trackPracticeEntryPointClicked } from "services/api/app-events/practiceEvents";
import { usePracticeNavigation } from "stores/practice/practice-navigation-context";

const GURUJUARA_PATH = "/gurujuara";
const GURUJUARA_TITLE = "Guru Juara";

const SMARTFEST_PATH = "/smartfest";
const SMARTFEST_TITLE = "SmartFest";

const Header: React.FC<IHeaderProps> = ({ hidesMenu = false }) => {
  const [isMenuVisible, setMenuVisible] = useState(false);
  const router = useRouter();
  const { user, signOutUser } = useAuth();
  const { requestPracticeNavigation } = usePracticeNavigation();
  const { userData, setUserData, isUserLoggedIn } = user || {};
  const isMobile = useMediaQuery("mobile");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_cookies, setCookie, removeCookie] = useCookies();
  const leadSource = getLeadSourceFromPath(router.pathname);
  const { contactMeForm } = useTanyaContext();
  const [isContactMeOpen, setIsContactMeOpen] = useState(false);
  const kelasLivePage = isKelasLivePage(router.pathname);
  const hideMenuItems = isMarketingPage(router.pathname);
  const isDaftarBimbelPage =
    getPageNameFromPath(router.pathname) === "Daftar Bimbel Page";
  const isDaftarBimbelColearnPage =
    getPageNameFromPath(router.pathname) === "Daftar Bimbel Colearn Page";

  const handleLoginEvent = () => {
    trackEvent({
      eventName: eventNames.loginButtonClicked,
      payload: {
        incomingSource: "website",
        eventSource: "website",
        loginFromPage: getPageNameFromPath(router.pathname),
        isOnboarded: null
      }
    });
    router.push({
      pathname: "/login",
      query: router.query
    });
  };

  const handleHamburgerIconClick = () => {
    setMenuVisible(!isMenuVisible);
  };

  /* istanbul ignore next -- unreachable: handleContactMeOpen always redirects to WhatsApp, so the ContactMe modal (and its onClose) never renders */
  const handleContactMeClose = () => {
    setIsContactMeOpen(false);
  };

  const handleCookieUpdate = (key: string, data: object) => {
    removeCookie(key, { path: "/" });
    setCookie(key, JSON.stringify(data), { path: "/" });
  };

  const handleContactMeOpen = () => {
    /* istanbul ignore else -- always-true guard; WA redirect is the only live path */
    if (true) {
      window.location.href =
        "https://api.whatsapp.com/send/?phone=6281919187201&text=Halo%2C+saya+minta+%23info+bimbel+online+Matematika+di+CoLearn&type=phone_number&app_absent=0";
    } else {
      setIsContactMeOpen(true);
    }
  };

  const handlePracticeEntryPointClick = () => {
    trackPracticeEntryPointClicked(
      userData?.primary_user?.access_token,
      isMobile ? "mobile_header" : "desktop_header"
    );
  };

  const handleNavigationClick = (event: React.MouseEvent<HTMLElement>) => {
    const link = (event.target as HTMLElement).closest("a[href]");
    const destination = link?.getAttribute("href");

    if (
      !destination ||
      destination.startsWith("http") ||
      destination.startsWith("#") ||
      link?.getAttribute("target") === "_blank"
    ) {
      return;
    }

    if (requestPracticeNavigation(destination)) {
      event.preventDefault();
      setMenuVisible(false);
    }
  };

  useEffect(() => {
    const fetchStudentData = async () => {
      try {
        if (userData?.primary_user?.access_token) {
          const userResponse = await getUserProfiles(
            userData.primary_user.access_token
          );
          const { data: user } = userResponse;

          const newUserData = {
            ...userData,
            ...user,
            primary_user: { ...userData.primary_user, ...user.primary_user },
            student_users: mergeArraysByKey(
              userData.student_users,
              user.student_users,
              "id"
            ),
            parent_user: { ...userData.parent_user, ...user.parent_user }
          };

          if (newUserData) {
            // Split the cookie because of the cookie size limit.
            const primaryAuthData: IPrimaryAuthData = { ...newUserData };
            delete primaryAuthData.student_users;
            delete primaryAuthData.parent_user;
            handleCookieUpdate(
              constants.userContext.cookieKey,
              primaryAuthData
            );
            handleCookieUpdate(constants.userContext.cookieKeyStudent, {
              student_users: newUserData.student_users
            });
            handleCookieUpdate(constants.userContext.cookieKeyParent, {
              parent_user: newUserData.parent_user
            });
            Sentry.setUser(null);
            Sentry.setUser({
              id: newUserData.primary_user.id,
              email: newUserData.primary_user.email,
              username: newUserData.primary_user.student_id,
              name: newUserData.primary_user.full_name
            });
            setUserData(newUserData);
          }
        }
      } catch (err) {
        Sentry.withScope((scope) => {
          scope.setTag("where", "Header");
          scope.setExtra("info", { step: "fetchStudentData" });
          Sentry.captureException(err);
        });
      }
    };

    fetchStudentData();
  }, []);

  return (
    <StyledHeader>
      <HeaderContainer onClick={handleNavigationClick}>
        {!hidesMenu && !hideMenuItems && (
          <HamburgerIcon
            onClick={handleHamburgerIconClick}
            data-testid="header-burger-button"
          >
            {isMenuVisible ? <Close /> : <Hamburger />}
          </HamburgerIcon>
        )}
        <Logo />
        {(kelasLivePage || isMobile) &&
          !isDaftarBimbelPage &&
          !isDaftarBimbelColearnPage && (
            <Link
              href="https://colearn.app.link/DownloadCoLearn"
              passHref
              legacyBehavior
            >
              <DownloadButton $isOpen={isMenuVisible}>Download</DownloadButton>
            </Link>
          )}

        {!hideMenuItems && (
          <MobileMenuContainer $active={isMenuVisible}>
            <div>
              {isUserLoggedIn && (
                <MobileUserProfileContainer>
                  <MobileUserProfile
                    userData={userData}
                    signOutUser={signOutUser}
                    setUserData={setUserData}
                  />
                </MobileUserProfileContainer>
              )}
            </div>
            <Menu>
              <MenuItem isSelected={router?.pathname?.startsWith("/bimbel")}>
                <MenuLink
                  data-testid="bimbel-header-button"
                  href="/bimbel"
                  title="Bimbel"
                >
                  Bimbel
                </MenuLink>
              </MenuItem>
              {isUserLoggedIn && (
                <MenuItem isSelected={router?.pathname === "/latihan"}>
                  <MenuLink
                    data-testid="practice-header-button"
                    href="/latihan"
                    title="Practice"
                    onClick={handlePracticeEntryPointClick}
                  >
                    Latihan <Badge label="Baru" size="sm" />
                  </MenuLink>
                </MenuItem>
              )}
              <MenuItem isSelected={router?.pathname === "/tanya"}>
                <MenuLink
                  data-testid="tanya-header-button"
                  href="/tanya"
                  title="Tanya"
                >
                  Tanya
                </MenuLink>
              </MenuItem>
              <MenuItem isSelected={router?.pathname === "/tutor"}>
                <MenuLink
                  data-testid="tutor-header-button"
                  href="/tutor"
                  title="Tutor"
                >
                  Tutor
                </MenuLink>
              </MenuItem>
            </Menu>
            <Menu>
              <MenuItem isSelected={router?.pathname === "/jadwal"}>
                <MenuLink
                  href="/jadwal"
                  title="Jadwal"
                  data-testid="jadwal-header-button"
                >
                  Jadwal dan Materi
                </MenuLink>
              </MenuItem>
              <MenuItem isSelected={router?.pathname === GURUJUARA_PATH}>
                <MenuLink
                  href={GURUJUARA_PATH}
                  title={GURUJUARA_TITLE}
                  data-testid="ngajar-header-button"
                >
                  {GURUJUARA_TITLE}
                </MenuLink>
              </MenuItem>
              <MenuItem isSelected={router?.pathname === SMARTFEST_PATH}>
                <MenuLink
                  href={SMARTFEST_PATH}
                  title={SMARTFEST_TITLE}
                  data-testid="ngajar-header-button"
                >
                  {SMARTFEST_TITLE}
                </MenuLink>
              </MenuItem>
              <Spacer height={25} />
              <MenuItem>
                <LeadGenerationButton onClick={handleContactMeOpen}>
                  Minta info bimbel
                  <Image
                    src={WhatAppIcon}
                    alt="Whatsapp Icon"
                    layout="fixed"
                    width={19}
                    height={19}
                  />
                </LeadGenerationButton>
              </MenuItem>
            </Menu>
            <Spacer height={16} />
            {isUserLoggedIn ? (
              <HeaderMenuCTA onClick={() => signOutUser()}>
                Keluar
              </HeaderMenuCTA>
            ) : (
              <Link
                href="https://colearn.app.link/DownloadCoLearn"
                passHref
                legacyBehavior
              >
                <DownloadButton2 $isFullWidth>Download</DownloadButton2>
              </Link>
            )}
          </MobileMenuContainer>
        )}

        {!hidesMenu && !hideMenuItems && (
          <>
            <MenuContainer>
              <Menu>
                <MenuItem isSelected={router?.pathname?.startsWith("/bimbel")}>
                  <MenuLink
                    data-testid="bimbel-header-button"
                    href="/bimbel"
                    title="Bimbel"
                  >
                    Bimbel
                  </MenuLink>
                </MenuItem>
                {isUserLoggedIn && (
                  <MenuItem isSelected={router?.pathname === "/latihan"}>
                    <MenuLink
                      data-testid="practice-header-button"
                      title="Latihan"
                      href="/latihan"
                      onClick={handlePracticeEntryPointClick}
                    >
                      Latihan <Badge label="Baru" size="sm" />
                    </MenuLink>
                  </MenuItem>
                )}
                <MenuItem isSelected={router?.pathname === "/tanya"}>
                  <MenuLink
                    data-testid="tanya-header-button"
                    title="Tanya"
                    href="/tanya"
                  >
                    Tanya
                  </MenuLink>
                </MenuItem>
                <MenuItem isSelected={router?.pathname === "/tutor"}>
                  <MenuLink
                    data-testid="tutor-header-button"
                    title="Tutor"
                    href="/tutor"
                  >
                    Tutor
                  </MenuLink>
                </MenuItem>
              </Menu>
              <Menu>
                <MenuItem isSelected={router?.pathname === "/jadwal"}>
                  <MenuLink
                    href="/jadwal"
                    title="Jadwal"
                    data-testid="jadwal-header-button"
                  >
                    Jadwal dan Materi
                  </MenuLink>
                </MenuItem>
                <MenuItem isSelected={router?.pathname === GURUJUARA_PATH}>
                  <MenuLink
                    href={GURUJUARA_PATH}
                    title={GURUJUARA_TITLE}
                    data-testid="ngajar-header-button"
                  >
                    {GURUJUARA_TITLE}
                  </MenuLink>
                </MenuItem>
                <MenuItem isSelected={router?.pathname === SMARTFEST_PATH}>
                  <MenuLink
                    href={SMARTFEST_PATH}
                    title={SMARTFEST_TITLE}
                    data-testid="ngajar-header-button"
                  >
                    {SMARTFEST_TITLE}
                  </MenuLink>
                </MenuItem>
              </Menu>
            </MenuContainer>
            {isContactMeOpen && (
              <ContactMe
                onClose={handleContactMeClose}
                buttonCTAText="Submit"
                metaData={contactMeForm.metaData}
                leadSource={leadSource}
              />
            )}
            {isUserLoggedIn ? (
              <UserProfile
                userData={userData}
                signOutUser={signOutUser}
                setUserData={setUserData}
              />
            ) : (
              !isMenuVisible &&
              !isMobile && (
                <HeaderCTA $placeRight onClick={handleLoginEvent}>
                  Masuk
                </HeaderCTA>
              )
            )}
          </>
        )}
      </HeaderContainer>
    </StyledHeader>
  );
};

export { Header };
